import { useQueries } from '@tanstack/react-query';
import { toMessageCards, toPlainQuote } from './doc-answer';
import type { AnswerSourceItem, AssistantMessageLike } from './doc-answer';
import { formatAnswerCopyText } from './answer-copy';
import type { AnswerCopySegment } from './answer-copy';
import { useKnowledgeItemsById } from '../../hooks/knowledge-items.hooks';
import { hubItemQuery } from '../../hooks/hub.hooks';
import { useChunks } from '../citations/chunks.hooks';

interface AnswerCopyOptions {
  isHub?: boolean;
}

function useHubItemsById(ids: string[]): Record<string, AnswerSourceItem | undefined> {
  return useQueries({
    queries: ids.map(hubItemQuery),
    combine: results =>
      Object.fromEntries(results.map((result, index) => [ids[index], result.data])),
  });
}

/**
 * Clipboard-ready plain text of a whole assistant answer: intro, then each doc
 * answer with its source title and the quotes its inline markers cite. Source
 * rows and quote content are fetched (cached per id), so a copy taken before
 * they resolve omits that source name or quote.
 *
 * @param message - The assistant message.
 * @param lang - `AI_CHAT` strings (`PANDA_COPY_SOURCE_LABEL`, `PANDA_COPY_CITATIONS_LABEL`).
 * @param options - `isHub` when the message is a hub answer, whose sources are cross-org items
 *   readable only through the hub copy. Default `false`.
 * @returns The text to put on the clipboard.
 * @example
 * const copyText = useAnswerCopyText(message, lang, { isHub: Boolean(hubSource) });
 */
export function useAnswerCopyText(
  message: AssistantMessageLike,
  lang: Record<string, string>,
  { isHub = false }: AnswerCopyOptions = {},
) {
  const cards = toMessageCards(message);
  const chunkIds = [...new Set(cards.flatMap(card => card.chunkIds))];
  const { chunks } = useChunks(chunkIds);
  const quotesByChunkId = Object.fromEntries(
    chunks.filter(chunk => chunk.content).map(chunk => [chunk.id, toPlainQuote(chunk.content)]),
  );
  const documentIds = [...new Set(cards.map(card => card.documentId))];
  const { itemsById: ownItemsById } = useKnowledgeItemsById(isHub ? [] : documentIds);
  const hubItemsById = useHubItemsById(isHub ? documentIds : []);
  const itemsById: Record<string, AnswerSourceItem | undefined> = isHub
    ? hubItemsById
    : ownItemsById;

  const segments: AnswerCopySegment[] = cards.map(card => ({
    context: card.context,
    text: card.text,
    sourceName: itemsById[card.documentId]?.title,
    citations: card.chunkIds.flatMap(chunkId => quotesByChunkId[chunkId] ?? []),
  }));
  return formatAnswerCopyText(message.content, segments, {
    source: lang.PANDA_COPY_SOURCE_LABEL || 'Source',
    citations: lang.PANDA_COPY_CITATIONS_LABEL || 'Citations',
  });
}
